import { isEqual, maxBy, minBy, uniq } from 'lodash'
import { Node as ProseNode, ResolvedPos } from 'prosemirror-model'
import { EditorState, NodeSelection, Transaction } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { findTableFromPos, findTableFromSel, isTableSelection } from '../../utils/table'
import { CellSelection } from '../../utils/table/CellSelection'
import { TableMap } from '../../utils/table/TableMap'
import { tableSelectionPluginKey } from './tableSelectionPluginKey'

const allCellsOfSameType = (selection: CellSelection) => {
  const allOfSame = selection.$includedCells.every(
    (cell) => cell.nodeAfter?.type === selection.$anchorCell.nodeAfter?.type,
  )
  return allOfSame
}

export const canMergeCells = (selection: CellSelection) => {
  return selection.$includedCells.length > 1 && allCellsOfSameType(selection) && isRectangularCellSelection(selection)
}

export const getTableMapFromSelection = (selection: CellSelection) => {
  const tableNode = findTableFromSel(selection)
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return new TableMap({ posBeforeTable: tableNode!.pos, node: tableNode!.node })
}

export const isRectangularCellSelection = (selection: CellSelection): boolean => {
  const tableMap = getTableMapFromSelection(selection)
  const posList = selection.$includedCells.map((cell) => cell.pos)
  const positions = maybeRectangularizePoslist(tableMap, posList)
  return posList.length === positions.length
}

function boundsOfPosList(tableMap: TableMap, posList: number[]) {
  const selectedCoords = posList.map((pos) => tableMap.getCoordListOfPos(pos)).flat(1)

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const minX = minBy(selectedCoords, (v) => v[1])![1],
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    minY = minBy(selectedCoords, (v) => v[0])![0],
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    maxX = maxBy(selectedCoords, (v) => v[1])![1],
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    maxY = maxBy(selectedCoords, (v) => v[0])![0]

  return { minX, minY, maxX, maxY }
}

export const maybeRectangularizePoslist = (tableMap: TableMap, posList: number[]): number[] => {
  const { minX, minY, maxX, maxY } = boundsOfPosList(tableMap, posList)

  let coordList: Array<[number, number]> = []
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const pos = tableMap!.getPosAtCoord(x, y)!
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const currentCoords = tableMap!.getCoordListOfPos(pos)
      coordList = coordList.concat(currentCoords)
    }
  }
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return uniq(coordList.map(([y, x]) => tableMap.getPosAtCoord(x, y)!))
}

/**
 * This helper method is used to guarantee that freeform selections are rectangular, taking into
 * account merged cells
 *
 * Given a list of positions, return those positions as well as any other positions necessary to
 * form a rectangle
 *
 * @parameter posList A list of positions (guaranteed to be included in the return array)
 */
export function calculateSelectionRect(tableMap: TableMap, posList: number[]): number[] {
  let positions: number[]
  do {
    positions = maybeRectangularizePoslist(tableMap, posList)
  } while (posList.length !== positions.length && (posList = positions))

  return positions
}

// Sets the dom selection to the table node. This provides a consistent dom selection,
// therefore preventing PM-view from changing the PM selection via selectionFromDom().
export function setDomSelection(tableDomNode: Node) {
  const domSelection = window.getSelection()
  if (domSelection == null) return
  const currentRange = domSelection.rangeCount === 1 && domSelection.getRangeAt(0)
  if (
    !currentRange ||
    currentRange.startContainer !== tableDomNode ||
    currentRange.endContainer !== tableDomNode ||
    currentRange.startOffset !== 0 ||
    currentRange.endOffset !== tableDomNode.childNodes.length
  ) {
    domSelection.removeAllRanges()
    const newRange = document.createRange()
    newRange.selectNodeContents(tableDomNode)
    domSelection.addRange(newRange)
  }
}

export function handleMouseDown(view: EditorView, startEvent: MouseEvent) {
  if (startEvent.ctrlKey || startEvent.metaKey) return

  let startDOMCell = domInCell(view, startEvent.target as HTMLElement),
    $anchor
  if (startEvent.shiftKey && view.state.selection instanceof CellSelection) {
    // Adding to an existing cell selection
    setCellSelection(view.state.selection.$anchorCell, startEvent)
    startEvent.preventDefault()
  } else if (
    startEvent.shiftKey &&
    startDOMCell &&
    ($anchor = cellAround(view.state.selection.$anchor)) !== null &&
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    cellUnderMouse(view, startEvent)!.pos !== $anchor.pos
  ) {
    // Adding to a selection that starts in another cell (causing a
    // cell selection to be created).
    setCellSelection($anchor, startEvent)
    startEvent.preventDefault()
  } else if (!startDOMCell) {
    // Not in a cell, let the default behavior happen.
    return
  }

  function setCellSelection($anchor: ResolvedPos, event: MouseEvent) {
    let $head = cellUnderMouse(view, event)
    let starting = tableSelectionPluginKey.getState(view.state).anchorPosOrMouseHandlingSignal === null
    if (!$head || !inSameTable($anchor, $head)) {
      if (starting) $head = $anchor
      else return
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const table = findTableFromPos($anchor.pos, view.state.doc)!
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })

    const resolvedPositions = calculateSelectionRect(tableMap, [$anchor.pos, $head.pos]).map((pos) =>
      view.state.doc.resolve(pos),
    )

    let selection = new CellSelection($anchor, $head, resolvedPositions)
    if (starting || !view.state.selection.eq(selection)) {
      let tr = view.state.tr.setSelection(selection)
      if (starting) tr.setMeta(tableSelectionPluginKey, { anchorPosOrMouseHandlingSignal: $anchor.pos })
      view.dispatch(tr)
    }
  }

  function mouseUp() {
    const selection = view.state.selection
    if (selection instanceof CellSelection) {
      const { node } = view.domAtPos(selection.$includedCells[0].start(-1))
      setDomSelection(node)
    }

    stop()
  }

  function dragStart(event: Event) {
    const selection = view.state.selection
    if (selection instanceof CellSelection || isTableSelection(selection)) {
      // Prevents dragging cell selections. stop() should not be called so that a new selection may start.
      event.preventDefault()
    } else {
      stop()
    }
  }

  // Stop listening to mouse motion events.
  function stop() {
    view.root.removeEventListener('mouseup', mouseUp)
    view.root.removeEventListener('dragstart', dragStart)
    view.root.removeEventListener('mousemove', move)
    if (tableSelectionPluginKey.getState(view.state).anchorPosOrMouseHandlingSignal !== null)
      view.dispatch(view.state.tr.setMeta(tableSelectionPluginKey, { anchorPosOrMouseHandlingSignal: -1 }))
  }

  function move(event: Event) {
    let anchor = tableSelectionPluginKey.getState(view.state).anchorPosOrMouseHandlingSignal,
      $anchor
    if (anchor != null) {
      // Continuing an existing cross-cell selection
      $anchor = view.state.doc.resolve(anchor)
    } else if (domInCell(view, event.target as HTMLElement) !== startDOMCell) {
      // Moving out of the initial cell -- start a new cell selection
      $anchor = cellUnderMouse(view, startEvent)
      if (!$anchor) return stop()
    }
    if ($anchor) setCellSelection($anchor, event as MouseEvent)
  }
  view.root.addEventListener('mouseup', mouseUp)
  view.root.addEventListener('dragstart', dragStart)
  view.root.addEventListener('mousemove', move)
}

export function handleTripleClick(view: EditorView, pos: number) {
  let doc = view.state.doc,
    $cell = cellAround(doc.resolve(pos))
  if (!$cell) return false
  view.dispatch(view.state.tr.setSelection(new CellSelection($cell, $cell)))
  return true
}

function domInCell(view: EditorView, dom: EditorView['dom']) {
  for (; dom && dom !== view.dom; dom = dom.parentNode as HTMLElement)
    if (dom.nodeName === 'TD' || dom.nodeName === 'TH') return dom
}
export function cellUnderMouse(view: EditorView, event: MouseEvent) {
  let mousePos = view.posAtCoords({ left: event.clientX, top: event.clientY })
  if (!mousePos) return null
  return mousePos ? cellAround(view.state.doc.resolve(mousePos.pos)) : null
}

export function cellAround($pos: ResolvedPos) {
  for (let d = $pos.depth - 1; d > 0; d--)
    if ($pos.node(d).type.spec.tableRole === 'row') return $pos.node(0).resolve($pos.before(d + 1))
  return null
}

export function inSameTable($a: ResolvedPos, $b: ResolvedPos) {
  return $a.depth === $b.depth && $a.pos >= $b.start(-1) && $a.pos <= $b.end(-1)
}

export function normalizeSelection(state: EditorState): Transaction | void {
  if (state.selection instanceof CellSelection) {
    const { $anchorCell, $includedCells } = state.selection

    const tableNode: ProseNode = $anchorCell.node(-1)
    const posBeforeTable: number = $anchorCell.before($anchorCell.depth - 1)
    const tableMap = new TableMap({ posBeforeTable, node: tableNode })
    const posBeforeSelectedCellList = $includedCells.map((ic) => ic.pos)

    if (isEqual(posBeforeSelectedCellList.sort(), tableMap.getPosBeforeCellList().sort())) {
      // whole table is selected
      return state.tr.setSelection(NodeSelection.create(state.doc, posBeforeTable))
    }
  }
}
